import {Dispatch, SetStateAction, createElement, useMemo, useState} from 'react';
import './style.scss';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {
  Stack,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
} from '@mui/material';
import {SxProps} from '@mui/system';
import moment from 'moment';
import {HandleCareTaskModal} from './modals/handle-task';
import {RegisterCell, ResponsibleCell, StatusCell, TextCell, TimeCell} from './cells';
import {SortIcon} from './sort-icon';
import {getTaskStatus} from '../shared/badges/task-status';
import {CareTaskComplementary} from '../../backend/interfaces/care-task-complementary';
import {CaregiverTaskCompletion} from '../../enums/caregiver-task-completion';
import {UserPublicInfo} from '../../backend/responses/user-public-info';
import {tableSx} from '../../theme/table-sx';
import {PendingTaskModal} from '../../screens/private/office/professional-caregiver/planner/pending-tasks/modals/pending-task-modal';
import {useAuth} from '../../contexts/auth/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../utils/constants';
import {createCareTaskRegisterText} from '../../utils/data/export-data/care-and-activities/export-data-care-and-activities';

type SortDirection = 'asc' | 'desc';
const DEFAULT_ORDER: SortDirection = 'asc';
type ColumnsId = 'time' | 'activity' | 'task' | 'register' | 'responsible' | 'status';

export type CellProps = {
  value: any;
  task: CareTaskComplementary;
  professionals: {
    [key: string]: UserPublicInfo;
  };
};

type CareTableProps = {
  isLoading: boolean;
  careTasks: Array<CareTaskComplementary> | undefined;
  openHandleTaskModal: (taskInfo: CareTaskComplementary) => void;
  professionals: {[key: string]: UserPublicInfo};
};

function CareTable({isLoading, careTasks, openHandleTaskModal, professionals}: CareTableProps) {
  const [order, setOrder] = useState<SortDirection>(DEFAULT_ORDER);
  const [orderBy, setOrderBy] = useState<ColumnsId>('time');
  const {t} = useTranslation();
  const {hasReadPermission} = useAuth();
  const canReadBADL = hasReadPermission(RolePermissionType.Seniors_CareAndActivities_BADL);
  const canReadIADL = hasReadPermission(RolePermissionType.Seniors_CareAndActivities_IADL);
  const canReadClinicalSupport = hasReadPermission(
    RolePermissionType.Seniors_CareAndActivities_ClinicalSupport
  );
  const canReadPersonalDevelopment = hasReadPermission(
    RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment
  );

  const columns: Record<
    ColumnsId,
    {
      label: string;
      sortable: boolean;
      sx?: SxProps<Theme>;
    }
  > = {
    time: {
      label: t(translations.care_and_activities.date),
      sortable: true,
      sx: {
        minWidth: '120px',
        maxWidth: {xs: '150px', xl: 'unset'},
      },
    },
    activity: {
      label: t(translations.care_and_activities.activity),
      sortable: true,
      sx: {
        minWidth: '150px',
        maxWidth: '200px',
      },
    },
    task: {
      label: t(translations.care_and_activities.task),
      sortable: true,
      sx: {
        minWidth: '160px',
        maxWidth: '200px',
      },
    },
    register: {
      label: t(translations.care_and_activities.register),
      sortable: true,
      sx: {
        minWidth: '80px',
        maxWidth: {xs: '120px', xl: 'unset'},
      },
    },
    responsible: {
      label: t(translations.care_and_activities.responsible),
      sortable: false,
      sx: {
        minWidth: '120px',
        maxWidth: {xs: '220px', xl: 'unset'},
      },
    },
    status: {
      label: t(translations.care_and_activities.status),
      sortable: true,
      sx: {
        minWidth: '140px',
        maxWidth: {xs: '200px', xl: 'unset'},
      },
    },
  };

  const columnsData = useMemo(() => {
    const mappedData =
      careTasks && careTasks.length > 0
        ? careTasks.map((row) => {
            const registerValue = createCareTaskRegisterText(t, row.careTask);

            return {
              time: {value: row.careTask.startsAt, row},
              activity: {value: row.activitiesGroupName, row},
              task: {
                value: row.careTask.nameConfigurationValue
                  ? row.careTask.nameConfigurationValue
                  : row.activityTypeName,
                row,
              },
              register: {
                value: registerValue ?? '',
                row,
              },
              responsible: {
                value: '',
                row,
              },
              status: {
                value: getTaskStatus(row) as string,
                row,
              },
            };
          })
        : undefined;

    if (mappedData) {
      return [...mappedData].sort((a, b) => {
        if (orderBy === 'time') {
          const dateA = moment(a[orderBy].value, 'YYYY-MM-DD HH:mm');
          const dateB = moment(b[orderBy].value, 'YYYY-MM-DD HH:mm');

          if (dateA.isBefore(dateB)) return order === 'asc' ? -1 : 1;
          if (dateA.isAfter(dateB)) return order === 'asc' ? 1 : -1;
          return 0;
        } else if (orderBy === 'status') {
          const statusLabelA = t(
            translations.care_and_activities.status_types[
              a[orderBy].value as CaregiverTaskCompletion
            ]
          );
          const statusLabelB = t(
            translations.care_and_activities.status_types[
              b[orderBy].value as CaregiverTaskCompletion
            ]
          );

          return (order === 'asc' ? 1 : -1) * statusLabelA.localeCompare(statusLabelB || '');
        } else {
          return (
            (order === 'asc' ? 1 : -1) * a[orderBy]?.value?.localeCompare(b[orderBy]?.value || '')
          );
        }
      });
    }

    return mappedData;
  }, [careTasks, order, orderBy, t]);

  const cellComponent: Record<ColumnsId, ({value, task, professionals}: CellProps) => JSX.Element> =
    {
      time: TimeCell,
      activity: TextCell,
      task: TextCell,
      register: RegisterCell,
      responsible: ResponsibleCell,
      status: StatusCell,
    };

  const contentRowColor = (status: CaregiverTaskCompletion): string => {
    let backgroundColor = 'transparent';

    switch (status) {
      case CaregiverTaskCompletion.DONE:
        backgroundColor = 'var(--dcm-green-handled)';
        break;
      case CaregiverTaskCompletion.MISSED:
        backgroundColor = 'var(--dcm-yellow-missed)';
        break;
      case CaregiverTaskCompletion.NOT_DONE:
        backgroundColor = 'var(--dcm-red-not-handled)';
        break;
      default:
        backgroundColor = 'transparent';
        break;
    }

    return backgroundColor;
  };

  const handleColumnSort = (columnId: ColumnsId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  if (!isLoading && !columnsData) {
    if (!canReadBADL && !canReadIADL && !canReadClinicalSupport && !canReadPersonalDevelopment) {
      return (
        <Stack className="care-table-empty" justifyContent="center" alignItems="center">
          <h3 className="care-table-empty-title">
            {t(translations.care_and_activities.permissions)}
          </h3>
        </Stack>
      );
    }
    return (
      <Stack className="care-table-empty" justifyContent="center" alignItems="center">
        <h3 className="care-table-empty-title">
          {t(translations.care_and_activities.empty_state)}
        </h3>
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} sx={tableSx.container}>
      <Table stickyHeader sx={tableSx.table}>
        <TableHead>
          <TableRow sx={tableSx.header}>
            {Object.entries(columns).map(([key, {label, sortable, sx}], index) => {
              const columnId = key as ColumnsId;
              const active = orderBy === columnId;
              const sortDirection = active ? order : 'asc';

              return (
                <TableCell
                  key={`cell_for_${columnId}_${index}`}
                  sortDirection={active ? order : undefined}
                  sx={sx}
                >
                  {sortable ? (
                    <TableSortLabel
                      active={active}
                      direction={sortDirection}
                      onClick={() => handleColumnSort(columnId)}
                      IconComponent={() => <SortIcon active={active} direction={sortDirection} />}
                      sx={{
                        fontWeight: 500,
                        '&.Mui-active': {
                          color: 'var(--dcm-gray)',
                        },
                      }}
                    >
                      {label}
                    </TableSortLabel>
                  ) : (
                    <span className="care-table-header-cell-label">{label}</span>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? new Array(6).fill(1).map((_item, index) => (
                <TableRow key={`skeleton_${index}`}>
                  <TableCell colSpan={6} sx={{padding: '0 10px'}}>
                    <Skeleton animation="wave" sx={{transform: 'scale(1, 0.78)'}} height={40} />
                  </TableCell>
                </TableRow>
              ))
            : columnsData &&
              columnsData.map((tableRow, index) => {
                return (
                  <TableRow
                    key={`table_row_${index}`}
                    sx={{
                      ...tableSx.row,
                      backgroundColor: contentRowColor(getTaskStatus(tableRow.task.row)),
                    }}
                    onClick={() => openHandleTaskModal(tableRow.task.row)}
                  >
                    {Object.entries(tableRow).map(([key, cell]) => {
                      const columnId = key as ColumnsId;
                      return (
                        <TableCell sx={columns[columnId].sx} key={`table_cell_${columnId}`}>
                          {createElement<CellProps>(cellComponent[columnId], {
                            value: cell.value,
                            task: cell.row,
                            professionals,
                          })}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface TaskTableProps {
  careTasks: CareTaskComplementary[] | undefined;
  setCareTasks: Dispatch<SetStateAction<CareTaskComplementary[] | undefined>>;
  isLoading: boolean;
  professionals: {
    [key: string]: UserPublicInfo;
  };
}

export const TaskTable = ({careTasks, setCareTasks, isLoading, professionals}: TaskTableProps) => {
  const [modalTask, setModalTask] = useState<CareTaskComplementary | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingValidationModalOpen, setPendingValidationModalOpen] = useState(false);
  const {hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Seniors_CareAndActivities);
  const canWriteBADL = hasWritePermission(RolePermissionType.Seniors_CareAndActivities_BADL);
  const canWriteIADL = hasWritePermission(RolePermissionType.Seniors_CareAndActivities_IADL);
  const canWriteClinicalSupport = hasWritePermission(
    RolePermissionType.Seniors_CareAndActivities_ClinicalSupport
  );
  const canWritePersonalDevelopment = hasWritePermission(
    RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment
  );
  const openHandleTaskModal = (task: CareTaskComplementary) => {
    setModalTask(task);
    if (task.careTask.pendingCareTaskId && !task.careTask.markedAsHandled) {
      setPendingValidationModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };
  const canWriteSector =
    (canWriteBADL && modalTask?.careTask.activitiesSectorId === BADL_SECTOR_ID) ||
    (canWriteIADL && modalTask?.careTask.activitiesSectorId === IADL_SECTOR_ID) ||
    (canWriteClinicalSupport &&
      modalTask?.careTask.activitiesSectorId === CLINICAL_SUPPORT_SECTOR_ID) ||
    (canWritePersonalDevelopment &&
      modalTask?.careTask.activitiesSectorId === PERSONAL_DEVELOPMENT_SECTOR_ID);

  return (
    <>
      {modalOpen && (
        <HandleCareTaskModal
          modalTask={modalTask}
          userId={modalTask?.careTask.userId || ''}
          careTasks={careTasks}
          setCareTasks={setCareTasks}
          open
          onClose={() => setModalOpen(false)}
          canWrite={canWrite && canWriteSector}
          displayPendingTime={
            modalTask &&
            !!modalTask.careTask.pendingCareTaskId &&
            !modalTask.careTask.markedAsHandled
          }
        />
      )}

      {pendingValidationModalOpen && (
        <PendingTaskModal
          open
          onValidate={() => {
            setModalOpen(true);
            setPendingValidationModalOpen(false);
          }}
          onClose={() => setPendingValidationModalOpen(false)}
        />
      )}

      <CareTable
        {...{
          isLoading,
          careTasks,
          openHandleTaskModal,
          professionals,
        }}
      />
    </>
  );
};
